/**
 * Postcard Widget Components
 *
 * Four web components for handling postcard functionality:
 * 1. PostcardWidget - Product page checkbox + message field + live preview
 * 2. PostcardMessageEditor - Cart inline message editor
 * 3. PostcardCartWidget - Cart cross-sell widget
 * 4. PostcardPreviewModal - Fullscreen 3D flip preview of the card
 */

// ============================================
// SHARED: Emoji strip regex
// Matches Unicode pictographic characters (emoji, dingbats, symbols)
// plus the variation selector used to render emoji-style glyphs.
// ============================================
const POSTCARD_EMOJI_REGEX = /[\p{Extended_Pictographic}\u{FE0F}]/gu;

function stripPostcardEmojis(text) {
  return text.replace(POSTCARD_EMOJI_REGEX, '');
}

// ============================================
// HELPER: Get bundled sections for cart refresh
// ============================================
function getBundledSections() {
  const sections = [];
  document.documentElement.dispatchEvent(
    new CustomEvent('cart:prepare-bundled-sections', {
      bubbles: true,
      detail: { sections }
    })
  );
  return sections;
}

// ============================================
// HELPER: Add to cart with section rendering
// ============================================
async function addToCartWithSections(variantId, message) {
  const sectionsToBundle = getBundledSections();

  const formData = new FormData();
  formData.set('id', variantId);
  formData.set('quantity', 1);
  formData.set('properties[Personlig Besked]', message);
  formData.set('properties[_postcard]', 'true');
  formData.set('sections', sectionsToBundle.join(','));
  formData.set('sections_url', window.location.pathname);

  const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
    method: 'POST',
    body: formData,
    headers: {
      'X-Requested-With': 'XMLHttpRequest'
    }
  });

  if (!response.ok) {
    throw new Error('Failed to add postcard to cart');
  }

  const responseJson = await response.json();

  // Fetch cart data and merge sections
  const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
  cartContent.sections = responseJson.sections;

  return cartContent;
}

// ============================================
// HELPER: Update cart item with section rendering
// ============================================
async function updateCartItemWithSections(lineKey, properties) {
  const sectionsToBundle = getBundledSections();

  const response = await fetch(`${Shopify.routes.root}cart/change.js`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest'
    },
    body: JSON.stringify({
      id: lineKey,
      properties: properties,
      sections: sectionsToBundle.join(','),
      sections_url: window.location.pathname
    })
  });

  if (!response.ok) {
    throw new Error('Failed to update cart item');
  }

  const cartContent = await response.json();
  return cartContent;
}

// ============================================
// POSTCARD WIDGET - Product Page
// ============================================
class PostcardWidget extends HTMLElement {
  constructor() {
    super();
    this.boundOnProductAdded = this.onProductAdded.bind(this);
    this.messageSaved = false;
  }

  connectedCallback() {
    this.checkbox = this.querySelector('input[type="checkbox"]');
    this.fields = this.querySelector('.postcard-widget__fields');
    this.messageInput = this.querySelector('textarea');
    this.saveButton = this.querySelector('.postcard-widget__save');
    this.statusEl = this.querySelector('.postcard-widget__status');

    // Preview elements (may not exist if show_preview is off)
    this.previewMessageEl = this.querySelector('[data-postcard-preview-message]');
    this.modalOpenButton = this.querySelector('[data-postcard-preview-open]');
    this.modalEl = this.querySelector('postcard-preview-modal');
    this.modalMessageEl = this.querySelector('[data-postcard-modal-message]');
    this.previewPlaceholder = this.previewMessageEl ? this.previewMessageEl.textContent.trim() : '';

    if (this.checkbox) {
      this.checkbox.addEventListener('change', this.toggleFields.bind(this));
    }

    if (this.saveButton) {
      this.saveButton.addEventListener('click', this.saveMessage.bind(this));
    }

    if (this.messageInput) {
      this.messageInput.addEventListener('input', this.onInput.bind(this));
    }

    if (this.modalOpenButton) {
      this.modalOpenButton.addEventListener('click', this.openModal.bind(this));
    }

    // Listen for main product form submission
    document.addEventListener('variant:add', this.boundOnProductAdded);
  }

  onInput() {
    if (!this.messageInput) return;

    // Strip emojis in place — preserves caret position at end of text
    const original = this.messageInput.value;
    const cleaned = stripPostcardEmojis(original);
    if (cleaned !== original) {
      const caret = this.messageInput.selectionStart;
      this.messageInput.value = cleaned;
      const newPos = Math.max(0, caret - (original.length - cleaned.length));
      try { this.messageInput.setSelectionRange(newPos, newPos); } catch (_) {}
    }

    this.updatePreview(cleaned);

    if (this.messageSaved) {
      this.messageSaved = false;
      this.hideStatus();
    }
  }

  updatePreview(text) {
    const value = (text || '').trim();
    const display = value.length > 0 ? text : this.previewPlaceholder;
    const isPlaceholder = value.length === 0;

    if (this.previewMessageEl) {
      this.previewMessageEl.textContent = display;
      this.previewMessageEl.classList.toggle('postcard-preview-card__message--placeholder', isPlaceholder);
    }
    if (this.modalMessageEl) {
      this.modalMessageEl.textContent = display;
      this.modalMessageEl.classList.toggle('postcard-preview-card__message--placeholder', isPlaceholder);
    }
  }

  openModal() {
    if (this.modalEl && typeof this.modalEl.open === 'function') {
      this.modalEl.open(this.messageInput ? this.messageInput.value : '');
    }
  }

  disconnectedCallback() {
    document.removeEventListener('variant:add', this.boundOnProductAdded);
  }

  toggleFields() {
    if (this.fields) {
      this.fields.hidden = !this.checkbox.checked;
      if (this.checkbox.checked && this.messageInput) {
        this.messageInput.focus();
      }
      // Reset saved state when unchecking
      if (!this.checkbox.checked) {
        this.messageSaved = false;
        this.hideStatus();
        this.updatePreview('');
      }
    }
  }

  saveMessage() {
    if (!this.messageInput) return;

    // Mark as saved
    this.messageSaved = true;

    // Show confirmation
    this.showStatus();

    // Visual feedback on button
    if (this.saveButton) {
      this.saveButton.setAttribute('aria-busy', 'true');
      setTimeout(() => {
        this.saveButton.removeAttribute('aria-busy');
      }, 300);
    }
  }

  showStatus() {
    if (this.statusEl) {
      this.statusEl.classList.add('is-visible');
    }
  }

  hideStatus() {
    if (this.statusEl) {
      this.statusEl.classList.remove('is-visible');
    }
  }

  async onProductAdded(event) {
    // Only add postcard if checkbox is checked
    if (!this.checkbox || !this.checkbox.checked) return;

    const message = this.messageInput?.value || '';
    const variantId = this.dataset.postcardVariantId;

    if (variantId) {
      try {
        const cartContent = await addToCartWithSections(variantId, message);

        // Dispatch cart change event with sections data
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: {
            cart: cartContent,
            baseEvent: 'variant:add'
          }
        }));
      } catch (error) {
        console.error('Error adding postcard to cart:', error);
      }

      // Reset the widget after adding
      this.checkbox.checked = false;
      if (this.messageInput) this.messageInput.value = '';
      if (this.fields) this.fields.hidden = true;
      this.messageSaved = false;
      this.hideStatus();
      this.updatePreview('');
    }
  }
}

// ============================================
// POSTCARD MESSAGE EDITOR - Cart Inline
// ============================================
class PostcardMessageEditor extends HTMLElement {
  connectedCallback() {
    this.lineKey = this.dataset.lineKey;
    this.toggle = this.querySelector('.postcard-message-editor__toggle');
    this.content = this.querySelector('.postcard-message-editor__content');
    this.saveButton = this.querySelector('[data-save-message]');
    this.textarea = this.querySelector('textarea');
    this.statusEl = this.querySelector('.postcard-message-editor__status');

    if (this.toggle) {
      this.toggle.addEventListener('click', this.toggleContent.bind(this));
    }

    if (this.saveButton) {
      this.saveButton.addEventListener('click', this.saveMessage.bind(this));
    }

    // Save on blur as well
    if (this.textarea) {
      this.textarea.addEventListener('blur', () => {
        if (this.hasChanges) {
          this.saveMessage();
        }
      });

      this.textarea.addEventListener('input', () => {
        this.hasChanges = true;
      });
    }

    this.hasChanges = false;
  }

  toggleContent() {
    const expanded = this.toggle.getAttribute('aria-expanded') === 'true';
    this.toggle.setAttribute('aria-expanded', !expanded);

    if (this.content) {
      this.content.hidden = expanded;

      if (!expanded && this.textarea) {
        setTimeout(() => this.textarea.focus(), 50);
      }
    }
  }

  async saveMessage() {
    if (!this.lineKey || !this.textarea) return;

    const message = this.textarea.value;

    if (this.saveButton) {
      this.saveButton.setAttribute('aria-busy', 'true');
      this.saveButton.disabled = true;
    }

    try {
      const cartContent = await updateCartItemWithSections(this.lineKey, {
        'Personlig Besked': message,
        '_postcard': 'true'
      });

      // Show success status
      this.showStatus();
      this.hasChanges = false;

      // Dispatch cart change event
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true,
        detail: {
          cart: cartContent,
          baseEvent: 'postcard:update'
        }
      }));
    } catch (error) {
      console.error('Error saving postcard message:', error);
    } finally {
      if (this.saveButton) {
        this.saveButton.removeAttribute('aria-busy');
        this.saveButton.disabled = false;
      }
    }
  }

  showStatus() {
    if (this.statusEl) {
      this.statusEl.classList.add('is-visible');
      setTimeout(() => {
        this.statusEl.classList.remove('is-visible');
      }, 2000);
    }
  }
}

// ============================================
// POSTCARD CART WIDGET - Cross-sell
// ============================================
class PostcardCartWidget extends HTMLElement {
  connectedCallback() {
    this.addButton = this.querySelector('.postcard-cart-widget__add');
    this.fields = this.querySelector('.postcard-cart-widget__fields');
    this.textarea = this.querySelector('textarea');
    this.expanded = false;

    if (this.addButton) {
      this.addButton.addEventListener('click', this.handleClick.bind(this));
    }

    if (this.textarea) {
      this.textarea.addEventListener('input', this.onInput.bind(this));
    }
  }

  onInput() {
    if (!this.textarea) return;
    const original = this.textarea.value;
    const cleaned = stripPostcardEmojis(original);
    if (cleaned !== original) {
      const caret = this.textarea.selectionStart;
      this.textarea.value = cleaned;
      const newPos = Math.max(0, caret - (original.length - cleaned.length));
      try { this.textarea.setSelectionRange(newPos, newPos); } catch (_) {}
    }
  }

  handleClick() {
    if (!this.expanded) {
      // First click: expand fields
      if (this.fields) {
        this.fields.hidden = false;
        this.expanded = true;

        // Update button text
        const addToCartText = this.dataset.addToCartText || 'Add to cart';
        this.addButton.textContent = addToCartText;

        // Focus textarea
        if (this.textarea) {
          setTimeout(() => this.textarea.focus(), 50);
        }
      }
    } else {
      // Second click: add to cart
      this.addToCart();
    }
  }

  async addToCart() {
    const variantId = this.dataset.postcardVariantId;
    if (!variantId) return;

    const message = this.textarea?.value || '';

    if (this.addButton) {
      this.addButton.setAttribute('aria-busy', 'true');
      this.addButton.disabled = true;
    }

    try {
      const cartContent = await addToCartWithSections(variantId, message);

      // Dispatch cart change event to refresh cart drawer
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true,
        detail: {
          cart: cartContent,
          baseEvent: 'variant:add',
          onSuccessDo: 'force_open_drawer'
        }
      }));
    } catch (error) {
      console.error('Error adding postcard to cart:', error);
    } finally {
      if (this.addButton) {
        this.addButton.removeAttribute('aria-busy');
        this.addButton.disabled = false;
      }
    }
  }
}

// ============================================
// POSTCARD PREVIEW MODAL
// ============================================
class PostcardPreviewModal extends HTMLElement {
  constructor() {
    super();
    this.boundOnKeyDown = this.onKeyDown.bind(this);
    this.boundOnFocusIn = this.onFocusIn.bind(this);
  }

  connectedCallback() {
    this.flipper = this.querySelector('[data-postcard-modal-flipper]');
    this.flipButton = this.querySelector('[data-postcard-modal-flip]');
    this.stage = this.querySelector('[data-postcard-modal-stage]');
    this.messageEl = this.querySelector('[data-postcard-modal-message]');
    this.dismissTargets = this.querySelectorAll('[data-postcard-modal-dismiss]');
    this.returnFocusEl = null;
    this.isFlipped = false;
    // Capture the initial placeholder text so subsequent open() calls can restore it
    this.placeholderText = this.messageEl ? this.messageEl.textContent.trim() : '';

    if (this.flipButton) {
      this.flipButton.addEventListener('click', this.flip.bind(this));
      this.flipButton.setAttribute('aria-pressed', 'false');
    }
    if (this.stage) {
      this.stage.addEventListener('click', this.flip.bind(this));
    }
    this.dismissTargets.forEach((el) => {
      el.addEventListener('click', (event) => {
        event.stopPropagation();
        this.close();
      });
    });
  }

  open(message) {
    this.returnFocusEl = document.activeElement;

    const text = (message || '').trim();
    if (this.messageEl) {
      if (text.length > 0) {
        this.messageEl.textContent = message;
        this.messageEl.classList.remove('postcard-preview-card__message--placeholder');
      } else {
        this.messageEl.textContent = this.placeholderText;
        this.messageEl.classList.add('postcard-preview-card__message--placeholder');
      }
    }

    // Always open showing the front
    this.setFlipped(false);

    this.setAttribute('open', '');
    this.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', this.boundOnKeyDown);
    document.addEventListener('focusin', this.boundOnFocusIn);

    const firstFocusable = this.querySelector('.postcard-preview-modal__close');
    if (firstFocusable) firstFocusable.focus();
  }

  close() {
    this.removeAttribute('open');
    this.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    document.removeEventListener('keydown', this.boundOnKeyDown);
    document.removeEventListener('focusin', this.boundOnFocusIn);
    if (this.returnFocusEl && typeof this.returnFocusEl.focus === 'function') {
      this.returnFocusEl.focus();
    }
  }

  flip(event) {
    if (event) event.stopPropagation();
    this.setFlipped(!this.isFlipped);
  }

  setFlipped(flipped) {
    this.isFlipped = flipped;
    if (this.flipper) {
      this.flipper.classList.toggle('is-flipped', flipped);
    }
    if (this.flipButton) {
      this.flipButton.setAttribute('aria-pressed', flipped ? 'true' : 'false');
    }
  }

  onKeyDown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.key === 'Tab') {
      const focusables = this.querySelectorAll('button, [href], input, textarea, [tabindex]:not([tabindex="-1"])');
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  onFocusIn(event) {
    if (!this.hasAttribute('open')) return;
    if (!this.contains(event.target)) {
      const first = this.querySelector('.postcard-preview-modal__close');
      if (first) first.focus();
    }
  }
}

// ============================================
// REGISTER CUSTOM ELEMENTS
// ============================================
if (!customElements.get('postcard-widget')) {
  customElements.define('postcard-widget', PostcardWidget);
}

if (!customElements.get('postcard-message-editor')) {
  customElements.define('postcard-message-editor', PostcardMessageEditor);
}

if (!customElements.get('postcard-cart-widget')) {
  customElements.define('postcard-cart-widget', PostcardCartWidget);
}

if (!customElements.get('postcard-preview-modal')) {
  customElements.define('postcard-preview-modal', PostcardPreviewModal);
}
